import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Bell, Check, Clock, AlertCircle, RefreshCw, 
  User, Coffee, Utensils, CreditCard, HelpCircle, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const REQUEST_TYPES = {
  'call_waiter': { icon: Bell, label: 'Call Waiter', color: 'bg-blue-100 text-blue-700' },
  'order_ready': { icon: Utensils, label: 'Check Order', color: 'bg-amber-100 text-amber-700' },
  'bill_request': { icon: CreditCard, label: 'Request Bill', color: 'bg-green-100 text-green-700' },
  'refill': { icon: Coffee, label: 'Refill Request', color: 'bg-purple-100 text-purple-700' },
  'assistance': { icon: HelpCircle, label: 'Need Assistance', color: 'bg-orange-100 text-orange-700' },
};

export default function WaiterRequests() {
  const { api } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchRequests();
    // Poll for new requests every 10 seconds
    const interval = setInterval(fetchRequests, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await api.get('/waiter/requests');
      setRequests(response.data || []);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchRequests();
  };

  const acknowledgeRequest = async (sessionId, requestId) => {
    try {
      await api.put(`/table-sessions/${sessionId}/waiter-requests/${requestId}`, { 
        status: 'acknowledged' 
      });
      toast.success('Request acknowledged');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to acknowledge request');
    }
  };

  const completeRequest = async (sessionId, requestId) => {
    try {
      await api.put(`/table-sessions/${sessionId}/waiter-requests/${requestId}`, { 
        status: 'completed' 
      });
      toast.success('Request completed');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to complete request');
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getTimeSince = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const then = new Date(dateString);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    return `${diffHours} hours ago`;
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const acknowledgedRequests = requests.filter(r => r.status === 'acknowledged');
  const completedRequests = requests.filter(r => r.status === 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="waiter-requests">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Requests</h1>
          <p className="text-slate-500 mt-1">Customer requests from your tables</p>
        </div>
        <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-red-600">{pendingRequests.length}</p>
            <p className="text-sm text-slate-500">Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-amber-600">{acknowledgedRequests.length}</p>
            <p className="text-sm text-slate-500">In Progress</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-green-600">{completedRequests.length}</p>
            <p className="text-sm text-slate-500">Completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending Requests */}
      {pendingRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Pending Requests
            </CardTitle>
            <CardDescription>Requires your attention</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingRequests.map((request) => {
              const typeInfo = REQUEST_TYPES[request.type] || REQUEST_TYPES['call_waiter'];
              const TypeIcon = typeInfo.icon;
              
              return (
                <div 
                  key={request.id} 
                  className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-100"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${typeInfo.color}`}>
                      <TypeIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900">Table {request.table_number}</span>
                        <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                      </div>
                      <p className="text-sm text-slate-500">
                        {request.notes || 'No additional notes'} • {getTimeSince(request.created_at)}
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => acknowledgeRequest(request.session_id, request.id)}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Acknowledge
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* In Progress Requests */}
      {acknowledgedRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-600">
              <Clock className="w-5 h-5" />
              In Progress
            </CardTitle>
            <CardDescription>Requests you're handling</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {acknowledgedRequests.map((request) => {
              const typeInfo = REQUEST_TYPES[request.type] || REQUEST_TYPES['call_waiter'];
              const TypeIcon = typeInfo.icon;
              
              return (
                <div 
                  key={request.id} 
                  className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-100"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${typeInfo.color}`}>
                      <TypeIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900">Table {request.table_number}</span>
                        <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                      </div>
                      <p className="text-sm text-slate-500">
                        Acknowledged {getTimeSince(request.acknowledged_at)}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline"
                    onClick={() => completeRequest(request.session_id, request.id)}
                    className="border-green-500 text-green-600 hover:bg-green-50"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Complete
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* No Requests */}
      {requests.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <Bell className="w-16 h-16 mx-auto text-slate-200 mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No requests yet</h3>
            <p className="text-slate-500">
              Customer requests from your assigned tables will appear here
            </p>
          </CardContent>
        </Card>
      )}

      {/* Completed Requests (collapsed by default) */}
      {completedRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <Check className="w-5 h-5" />
              Recently Completed ({completedRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {completedRequests.slice(0, 5).map((request) => {
                const typeInfo = REQUEST_TYPES[request.type] || REQUEST_TYPES['call_waiter'];
                
                return (
                  <div 
                    key={request.id} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm"
                  >
                    <span className="text-slate-600">
                      Table {request.table_number} - {typeInfo.label}
                    </span>
                    <span className="text-slate-400">
                      {formatTime(request.completed_at)}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
